import React, { lazy, Suspense } from 'react'
import { Button, Card, colors, H2, Separator, Skeleton, useOverlay } from '@fs/zion-ui'
import { ContentUpload } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { type GroupSummary } from '@fs/zion-tree-types'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import ManageTreeList, { ManageTreeListSkeleton } from './ManageTreeList'
import { useUploadTree } from '../../../services/useUploadTree'

const UploadTreeOverlay = lazy(() => import('../import-page/UploadTreeOverlay'))

type Props = {
  groupTitle: string
  groups: GroupSummary[]
  isMobile: boolean
  isOwnedTreesGroup?: boolean
  processingFiles?: GedcomFile[]
  failedFiles?: GedcomFileFailed[]
}

function ManageTreeGroup({
  groupTitle,
  groups,
  isMobile,
  isOwnedTreesGroup,
  processingFiles,
  failedFiles,
}: Props): JSX.Element {
  const overlay = useOverlay()
  const [t] = useTranslation()
  const { maxTrees, disableCreateTreeButton, refetchData } = useUploadTree()

  return (
    <Card margins="xs" edges="border" backgroundColor={colors.gray00}>
      <H2 size="H5">{groupTitle}</H2>
      <Separator size="xs" />
      <ManageTreeList processingFiles={processingFiles} groups={groups} failedFiles={failedFiles} isMobile={isMobile} />
      {isOwnedTreesGroup && (
        <>
          <Separator size="xxs" />
          <Button
            disabled={disableCreateTreeButton}
            emphasis="medium"
            disabledDescription={
              maxTrees ? t('tree-import.create-tree-max-groups.error', { maxGroups: maxTrees }) : undefined
            }
            Icon={ContentUpload}
            data-testid="upload-gedcom-button.upload-gedcom.button"
            onClick={overlay.handleClick}
            linkName="Upload Gedcom Button"
          >
            {t('tree-import.create-files.button')}
          </Button>

          <Suspense fallback="">
            {(overlay.isOpen || overlay.transitioning) && (
              <UploadTreeOverlay overlay={overlay} onSuccess={refetchData} />
            )}
          </Suspense>
        </>
      )}
    </Card>
  )
}

export default ManageTreeGroup

export function ManageTreeGroupSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <Card margins="xs">
      <Skeleton.H5 />
      <Separator size="xs" />
      <ManageTreeListSkeleton isMobile={isMobile} />
    </Card>
  )
}
